// ============================================================================
// CANCUN100 — TestPDFPage.jsx
// Pestaña TEMPORAL de testing visual de los PDFs generados en navegador
// ============================================================================
// Propósito: validar que PDFCliente.jsx y PDFComision.jsx renderizan
// correctamente, comparándolos contra los PDFs actuales (PDFShift + Drive).
//
// USO:
//   - Accesible solo desde el dashboard de admin
//   - Botón "Descargar PDF Cliente prueba" → descarga PDF con datos MT-1136
//   - Botón "Descargar PDF Comisión prueba" → descarga PDF con datos MT-1136
//   - Comparar visualmente con los PDFs originales del cupón Juan Melas
//
// ⚠️ ESTA PESTAÑA SE ELIMINA cuando termine la migración (Fase 5A.6).
// ============================================================================

import React from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import PDFCliente from './PDFCliente';
import PDFComision from './PDFComision';

// Paleta del dashboard (consistente con App.js)
const C = {
  rosa:    '#f74780',
  naranja: '#ff8c42',
  card:    '#161625',
  surface: '#1c1c30',
  text:    '#e2e2ee',
  muted:   '#6b7a8d',
  border:  'rgba(255,255,255,0.06)',
  green:   '#00d4a1',
};

// ────────────────────────────────────────────────────────────────────────────
// Datos hardcoded del MT-1136 (Juan Melas) — para comparar lado a lado
// ────────────────────────────────────────────────────────────────────────────
const DATA_CLIENTE_MT1136 = {
  folio: 'MT-1136',
  fecha_creacion: '03-06-2026',
  cliente: {
    nombre:     'JUAN MELAS',
    hotel:      'HOTEL PIÑA',
    habitacion: '123',
    adultos:    2,
    menores:    0,
    infantes:   0,
  },
  servicios: [
    {
      nombre: '4X1 Clasico Tulum Coba',
      sku:    'SUE-005',
      proveedor_nombre: 'Suaste',
      destino: 'MOTOR LOBBY',
      fecha:   '2026-06-04',
    },
    {
      nombre: 'Entrada Xcaret Plus',
      sku:    'XPX-005',
      proveedor_nombre: 'Experiencias Xcaret Parques',
      destino: 'MOTOR LOBBY',
      fecha:   '2026-06-06',
    },
  ],
  resumen: {
    total:    9213,
    deposito: 3000,
    saldo:    6213,
    moneda:   'MXN',
  },
  pago: {
    metodo:         'Transferencia (5%)',
    notas:          'PAGA BALANCE',
    linea_contacto: 'CANCUN100',
    pago_balance:   'B1 — Balance se paga en primer tour',
  },
  representante: {
    nombre:   'Marco Trujillo',
    telefono: '+529987664046',
  },
};

const DATA_COMISION_MT1136 = {
  folio: 'MT-1136',
  fecha_creacion: '03-06-2026 - 17:15',
  representante: {
    nombre:   'Marco Trujillo',
    telefono: '+529987664046',
  },
  cliente: {
    nombre:   'JUAN MELAS',
    hotel:    'HOTEL PIÑA',
    adultos:  2,
    menores:  0,
    infantes: 0,
  },
  pago: {
    metodo:         'Transferencia (5%)',
    notas:          'PAGA BALANCE',
    linea_contacto: 'CANCUN100',
    pago_balance:   'B1 — Balance se paga en primer tour',
  },
  fecha_servicio: '2026-06-03',
  servicios: [
    {
      sku:              'SUE-005',
      nombre:           '4X1 Clasico Tulum Coba',
      proveedor_nombre: 'Suaste',
      moneda_origen:    'MXN',
      precio_origen:    null,
      tasa_cambio:      null,
      pub_mxn:          3020,
      neto_mxn:         1620,
    },
    {
      sku:              'XPX-005',
      nombre:           'Entrada Xcaret Plus',
      proveedor_nombre: 'Experiencias Xcaret Parques',
      moneda_origen:    'USD',
      precio_origen:    179,
      tasa_cambio:      17.30,
      pub_mxn:          6193,
      neto_mxn:         5571,
    },
  ],
  desglose: {
    precio_venta:     9213,
    cargo_int:        439,
    costo_neto:       7191,
    margen:           1584,
    comision_agente:  634,
    ganancia_agencia: 950,
    comision_pct:     40,
    total:            9213,
    deposito:         3000,
    pendiente:        6213,
  },
};

// ────────────────────────────────────────────────────────────────────────────
// Componente
// ────────────────────────────────────────────────────────────────────────────
const TestPDFPage = () => {
  const btnBase = {
    background: `linear-gradient(135deg, ${C.rosa}, ${C.naranja})`,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '14px 22px',
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "'DM Sans', sans-serif",
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    marginRight: 12,
    marginBottom: 10,
  };

  return (
    <div style={{ padding: '20px 0' }}>
      <h2 style={{ color: C.text, fontFamily: "'Space Grotesk', sans-serif", margin: '0 0 6px 0' }}>
        🧪 Test PDFs (temporal)
      </h2>
      <div style={{ color: C.muted, fontSize: 12, marginBottom: 20 }}>
        Pestaña temporal de validación — se elimina al cerrar Fase 5A.
        Genera PDFs con datos hardcoded del MT-1136 (Juan Melas) para comparar
        con los PDFs actuales de PDFShift + Drive.
      </div>

      {/* Card Cliente */}
      <div style={{
        background: C.card,
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
        border: `1px solid ${C.border}`,
      }}>
        <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1.5, marginBottom: 4 }}>
          PDF CLIENTE
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>
          Comprobante de Reserva — MT-1136
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
          JUAN MELAS · 2 Adultos · MXN $9,213 total
        </div>

        <PDFDownloadLink
          document={<PDFCliente data={DATA_CLIENTE_MT1136} />}
          fileName="TEST_PDFCliente_MT-1136.pdf"
        >
          {({ loading }) => (
            <button style={btnBase} disabled={loading}>
              {loading ? 'Generando PDF…' : '📄 Descargar PDF Cliente prueba'}
            </button>
          )}
        </PDFDownloadLink>
      </div>

      {/* Card Comisión */}
      <div style={{
        background: C.card,
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
        border: `1px solid ${C.border}`,
      }}>
        <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1.5, marginBottom: 4 }}>
          PDF COMISIÓN (CONFIDENCIAL)
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>
          Reporte de Comisión — MT-1136
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
          Marco Trujillo · Comisión MXN $634 (40%) · Agencia MXN $950
        </div>

        <PDFDownloadLink
          document={<PDFComision data={DATA_COMISION_MT1136} />}
          fileName="TEST_PDFComision_MT-1136.pdf"
        >
          {({ loading }) => (
            <button style={btnBase} disabled={loading}>
              {loading ? 'Generando PDF…' : '💰 Descargar PDF Comisión prueba'}
            </button>
          )}
        </PDFDownloadLink>
      </div>

      {/* Tips de validación */}
      <div style={{
        background: C.surface,
        borderRadius: 8,
        padding: 16,
        marginTop: 24,
        border: `1px solid ${C.border}`,
      }}>
        <div style={{ fontSize: 11, color: C.green, letterSpacing: 1.5, marginBottom: 8 }}>
          ✓ QUÉ VALIDAR
        </div>
        <ul style={{ color: C.muted, fontSize: 12, lineHeight: 1.7, margin: 0, paddingLeft: 18 }}>
          <li>Logo aparece arriba a la izquierda y se ve nítido</li>
          <li>El folio MT-1136 está visible en grande, color rosa</li>
          <li>Bloque "RESUMEN DE PAGO" tiene gradiente rosa-naranja</li>
          <li>Datos del cliente y servicios coinciden con los hardcoded arriba</li>
          <li>El PDF NO se corta a la mitad de ningún bloque</li>
          <li>En el PDF Comisión: el bloque "Tu Comisión $634 / AGENCIA $950" tiene gradiente</li>
          <li>Aparece la leyenda "CONFIDENCIAL" al final del PDF Comisión</li>
        </ul>
      </div>
    </div>
  );
};

export default TestPDFPage;
